/**
 * property-row.tsx — Phase 050 per-property dispatcher.
 *
 * Selects the field primitive by the (effectiveValue type, isTokenBound,
 * propertyKey === 'class') triple. Wraps writes via `usePropertyEdit` per
 * D-050-06 mixed-cadence rules (debounced 200ms for text/number/class,
 * instant for boolean/token-picker).
 *
 * D-050-05 adaptive token-vs-literal mode:
 *   - effectiveValue shape `{ tokenRef: string }` → render `<TokenPickerField>`
 *     with a "Switch to literal" link below; the link click is the SOLE
 *     operator-acknowledged collapse gesture (SC#2 invariant).
 *   - literal effectiveValue → render the appropriate primitive
 *     (InlineToggleField / InlineNumberField / EditableClassField for `class`
 *     / InlineTextField for string fallback) with a "Switch to token" link
 *     to restore token mode.
 *
 * Cadence map (D-050-06):
 *   propertyKey === 'class'  → EditableClassField → commit (debounced 200ms)
 *   token picker change      → commitInstant({ tokenRef })
 *   InlineToggleField        → commitInstant(boolean)
 *   InlineNumberField        → commit(number) (debounced 200ms)
 *   InlineTextField fallback → commit(string) (debounced 200ms)
 *
 * NOTE on field primitive contracts (verified against
 * `src/features/page-editor/components/inspector/fields/*.tsx`):
 *   - InlineTextField requires `show: boolean` — we always pass `true`.
 *   - InlineTextField + EditableClassField commit on blur/Enter (internal
 *     local-draft state); usePropertyEdit's debounce layer composes on top of
 *     that — the operator's blur fires `commit(v)`, which schedules the
 *     200ms write.
 *   - InlineToggleField takes `checked` + `onChange(boolean)`.
 *   - InlineNumberField takes `value` + `onChange(number)`.
 */
import { useState } from 'react'
import {
  InlineTextField,
  InlineNumberField,
  InlineToggleField,
  EditableClassField,
} from '@/features/page-editor/components/inspector/fields'
import { usePropertyEdit } from './use-property-edit'
import { TokenPickerField } from './token-picker-field'

export interface PropertyRowProps {
  blockId: string
  propertyKey: string
  baselineValue: unknown
  draftValue: unknown
}

/**
 * Predicate — value is a token-binding shape `{ tokenRef: string }`. The
 * comparator + Phase 050 token-aware paths use this exclusively; do NOT
 * compare reference identity (drafts may be re-emitted across `key={tick}`
 * remounts).
 */
export function isTokenBound(value: unknown): value is { tokenRef: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'tokenRef' in value &&
    typeof (value as Record<string, unknown>).tokenRef === 'string'
  )
}

export function PropertyRow({
  blockId,
  propertyKey,
  baselineValue,
  draftValue,
}: PropertyRowProps) {
  // SC#2 invariant — operator-edited draft (if any) overrides baseline.
  const effectiveValue = draftValue !== undefined ? draftValue : baselineValue
  // D-050-05 mode follows the effectiveValue's initial shape; explicit
  // operator-toggle is the only way to flip after the first render.
  const initialMode: 'literal' | 'token' = isTokenBound(effectiveValue)
    ? 'token'
    : 'literal'
  const [mode, setMode] = useState<'literal' | 'token'>(initialMode)
  const { commit, commitInstant } = usePropertyEdit(blockId, propertyKey)

  // ── 'class' special case — always EditableClassField; cadence debounced. ──
  if (propertyKey === 'class') {
    return (
      <EditableClassField
        value={typeof effectiveValue === 'string' ? effectiveValue : ''}
        onChange={(v) => commit(v)}
      />
    )
  }

  // ── D-050-05 token mode — TokenPickerField + explicit "Switch to literal". ──
  if (mode === 'token' && isTokenBound(effectiveValue)) {
    return (
      <div className="flex flex-col gap-1">
        <TokenPickerField
          label={propertyKey}
          value={effectiveValue.tokenRef}
          onChange={(tokenRef) => commitInstant({ tokenRef })}
        />
        <button
          type="button"
          onClick={() => {
            // EXPLICIT operator gesture — this is the ONLY path that
            // collapses a tokenRef to literal in the panel UX (SC#2
            // invariant). We commit an empty string sentinel so the
            // draft store sees the collapse; the operator picks the
            // new literal next.
            setMode('literal')
            commitInstant('')
          }}
          className="text-xs text-muted-foreground hover:underline self-start"
        >
          Switch to literal
        </button>
      </div>
    )
  }

  // ── Literal mode — pick primitive by JS type. ──
  if (typeof effectiveValue === 'boolean') {
    return (
      <div className="flex flex-col gap-1">
        <InlineToggleField
          label={propertyKey}
          checked={effectiveValue}
          onChange={(v) => commitInstant(v)}
        />
        <button
          type="button"
          onClick={() => setMode('token')}
          className="text-xs text-muted-foreground hover:underline self-start"
        >
          Switch to token
        </button>
      </div>
    )
  }

  if (typeof effectiveValue === 'number') {
    return (
      <div className="flex flex-col gap-1">
        <InlineNumberField
          label={propertyKey}
          value={effectiveValue}
          onChange={(v) => commit(v)}
        />
        <button
          type="button"
          onClick={() => setMode('token')}
          className="text-xs text-muted-foreground hover:underline self-start"
        >
          Switch to token
        </button>
      </div>
    )
  }

  // String fallback — covers text / placeholder / iterate_over / label /
  // binding / level (heading enum).
  return (
    <div className="flex flex-col gap-1">
      <InlineTextField
        label={propertyKey}
        value={typeof effectiveValue === 'string' ? effectiveValue : ''}
        onChange={(v) => commit(v)}
        placeholder=""
        show={true}
      />
      <button
        type="button"
        onClick={() => setMode('token')}
        className="text-xs text-muted-foreground hover:underline self-start"
      >
        Switch to token
      </button>
    </div>
  )
}
